import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Configure,
  InstantSearchSSRProvider,
  useClearRefinements,
  useHierarchicalMenu,
  useHits,
  useMenu,
  useSearchBox,
} from 'react-instantsearch';
import { useRouter } from 'next/compat/router';
import dynamic from 'next/dynamic';
import { oneLine } from 'common-tags';
import { format } from 'date-fns';

import Calendar from '@/icons/light/calendar.svg';
import ArrowLeft from '@/icons/light/chevron-left.svg';
import ArrowRight from '@/icons/light/chevron-right.svg';
import Filters from '@/icons/light/hom-filters.svg';
import List from '@/icons/light/list.svg';
import MapPin from '@/icons/light/map-pin.svg';
import X from '@/icons/light/x.svg';
import { Container, CustomSelect } from '@/atoms';
import { ListingHeader, ListingViewBar } from '@/molecules';

import {
  FacetMultiSelect,
  FacetSelect,
  GridCardHits,
  HierarchicalMenu,
  InstantSearch,
  Pagination,
  SortBySelect,
  Title,
} from '@/components/algolia';
import { ClearLocationRefinements } from '@/components/algolia/clear-location-refinements';
import { useMedEstDates } from '@/components/algolia/connectors/useMedEstDates';
import { useNumericRangeFilter } from '@/components/algolia/connectors/useNumericRangeFilter';
import { buildFlatsRouting } from '@/components/algolia/flats-routing';
import {
  convertToURL,
  formatZoneName,
  priceFilterOptions,
} from '@/components/algolia/utils';
import { useResizeObserver } from '@/hooks';
import { routes, useIsDesktop, usePrevious } from '@/lib';
import { generatedListingConfig } from '@/lib/contexts/listing-config.generated';
import { gtmPush } from '@/utils';

import type { TypeFlatAnalytics } from '@/types';
import type { TypeFlat } from '@/types/analytics/flat';
import type { FunctionComponent } from 'react';
import type { TypeListOfFlatsProps } from './types';

// Bordes de la barra de filtros: el look HoM (input-border) va con el flag
// `filtersGrid`; sin él se conservan los grises neutrals de legacy.
const filtersBorderClass = generatedListingConfig.filtersGrid
  ? { toggle: 'border-input-border', divider: 'border-input-border' }
  : { toggle: 'border-neutrals-200', divider: 'border-neutrals-100' };

// ssr: false porque el componente carga internamente `LeafletMap` (ya
// ssr-false) y muestra su propio skeleton mientras llegan los datos.
// Renderizarlo en SSR provoca hydration mismatches: el HTML del
// servidor incluye el skeleton, pero en el cliente el chunk dinámico
// no está disponible en el primer render → la subtree desaparece y
// React rebuilds. Hacerlo client-only desde el principio elimina la
// divergencia.
const ClusteringMapAlgolia = dynamic(
  () => import('@/components/algolia/clustering-map'),
  {
    ssr: false,
  },
);

const Modal = dynamic(() => import('@/atoms/modal'), {
  ssr: true,
});

const CalendarTabs = dynamic(
  () => import('@/components/molecules/calendar-tabs'),
  {
    ssr: true,
  },
);

const calculateHeight = (element: HTMLElement | undefined) => {
  if (!element) return 0;

  const height = element.offsetHeight;
  const marginTop = parseInt(
    window?.getComputedStyle(element)?.getPropertyValue('margin-top') ?? 0,
  );
  // Este es el height del box shadow del Disclosure.Panel del HierarchicalMenu
  const BOX_SHADOW_HEIGHT = 12;

  return height + marginTop + BOX_SHADOW_HEIGHT;
};

const isMedEst = (type: TypeFlatAnalytics['ventaAlquiler']) =>
  type === 'MED_EST';

const INDEX_NAME =
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_FLATS_INDEX_NAME ?? '';

const ZONE_ATTRIBUTES = [
  'hierarchicalZonas.lvl0',
  'hierarchicalZonas.lvl1',
  'hierarchicalZonas.lvl2',
  'hierarchicalZonas.lvl3',
];

// Navegación de zona (Opción A): cambiar de zona en el dropdown navega al path
// de esa zona (SSR), reflejándola en la URL. `zone(...)` construye el path;
// `index` es el listado sin zona (para "quitar zona").
const ROUTE_ZONE: Record<TypeFlat['ventaAlquiler'], (z: string) => string> = {
  VENTA: routes.flats.sale.zone,
  ALQUILER: routes.flats.rent.zone,
  MED_EST: routes.flats.half.zone,
  ALQ_VENT: routes.flats.half.zone,
};
const ROUTE_INDEX: Record<TypeFlat['ventaAlquiler'], string> = {
  VENTA: routes.flats.sale.index,
  ALQUILER: routes.flats.rent.index,
  MED_EST: routes.flats.half.index,
  ALQ_VENT: routes.flats.half.index,
};

// Buckets del filtro "Tamaño mín." (metrosComputablesUA, el m² que muestran
// cards y ficha) y opciones del select de habitaciones de la barra.
// En desktop la barra es un grid de columnas iguales (ver el contenedor), así
// que cada selector sólo necesita permitir encogerse por debajo de su
// contenido: `min-w-0` evita que el texto largo desborde la columna. En mobile
// la fila scrollea y cada uno conserva su ancho natural.
const FILTER_WIDTH = 'lg:min-w-0';

/**
 * Columnas del grid de filtros en desktop: tantas como quepan, nunca más
 * estrechas que 168px.
 *
 * Ese mínimo sale de medir el control con la etiqueta más larga
 * ("Características" necesita 166px). Con un número fijo de columnas, en
 * viewports intermedios salían a 111px y todas las etiquetas quedaban en
 * "Preci...", "Tama...".
 *
 * `auto-fit` además se adapta solo a cuántos controles pinta cada tenant (los
 * legacy sólo muestran precios y zona) y a la columna extra de media estancia,
 * sin tener que contarlos.
 */
const GRID_COLS = 'lg:grid-cols-[repeat(auto-fit,minmax(168px,1fr))]';

const M2_MIN_OPTIONS = [40, 50, 60, 70, 80, 90, 100, 120, 150];
const HABITACIONES_OPTIONS = [1, 2, 3, 4];

// Entradas de `caracteristicasFacet.description` que vienen de SAP pero no
// son características de cara al usuario.
const CARACTERISTICAS_OCULTAS = ['Metros Cuadrados Computables'];

// Limpia sufijos internos de los labels SAP, p.ej.
// "Terrazas  (SI/en blanco)" → "Terrazas".
const cleanCaracteristicaLabel = (label: string) =>
  label
    .replace(/\s*\((?:SI\/)?en blanco\)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();

const getLastRefined = (items: any, fallback: string): string => {
  const item = items?.filter((it: any) => it.isRefined)[0];
  if (item && item.isRefined) {
    const hasChildrenRefined =
      item?.data?.filter((it: any) => it.isRefined)?.length > 0;
    if (hasChildrenRefined && item?.data) {
      return getLastRefined(item.data, fallback);
    }
    return item?.value ?? fallback;
  }
  return fallback;
};

/**
 * Igual que `getLastRefined` pero devuelve `null` si nada está
 * refinado — útil para el caso "el listado muestra todas las zonas" en
 * el ChromeHeader, donde no queremos mostrar la palabra "en" + zona
 * cuando no hay zona específica.
 */
const getRefinedArea = (items: any): string | null => {
  const item = items?.filter((it: any) => it.isRefined)[0];
  if (item && item.isRefined) {
    const hasChildrenRefined =
      item?.data?.filter((it: any) => it.isRefined)?.length > 0;
    if (hasChildrenRefined && item?.data) {
      return getRefinedArea(item.data);
    }
    return item?.value ?? null;
  }
  return null;
};

type ChromeHeaderProps = {
  defaultArea: string;
  typeText: string;
  eyebrow?: string | null;
  titleTemplate?: string | null;
  titleEstilo?: any;
};

const ChromeHeader: FunctionComponent<ChromeHeaderProps> = ({
  defaultArea,
  typeText,
  eyebrow,
  titleTemplate,
  titleEstilo,
}) => {
  const { items } = useHierarchicalMenu({ attributes: ZONE_ATTRIBUTES });
  // `titleTemplate` es la copia BASE sin conector ni zona, e.g.
  // "Viviendas en venta". Cuando hay zona específica, el frontend
  // añade " en <mark>{zona}</mark>" al final. Si no hay zona refinada,
  // se muestra sólo el template (sin "en" ni zona).
  const refinedArea = getRefinedArea(items);
  const baseTitle = titleTemplate ?? `Pisos ${typeText}`;
  // Si el usuario marcó la zona default literal, tratamos como "sin
  // refinar" para no mostrar "en todas las zonas".
  const hasSpecificArea = refinedArea && refinedArea !== defaultArea;
  const title = hasSpecificArea ? `${baseTitle} en ` : baseTitle;

  return (
    <ListingHeader
      eyebrow={eyebrow}
      title={title}
      area={hasSpecificArea ? formatZoneName(refinedArea) : undefined}
      titleEstilo={titleEstilo}
    />
  );
};

type ChromeViewBarProps = {
  className?: string;
  defaultArea: string;
  typeText: string;
  oneCol: boolean;
  onChange: (v: boolean) => void;
};

const ChromeViewBar: FunctionComponent<ChromeViewBarProps> = ({
  className,
  defaultArea,
  typeText,
  oneCol,
  onChange,
}) => {
  const { results } = useHits();
  const { items } = useHierarchicalMenu({ attributes: ZONE_ATTRIBUTES });
  const area = getLastRefined(items, defaultArea);
  // El fallback ("todas las zonas") no es un topónimo — no se formatea.
  const areaText = area === defaultArea ? area : formatZoneName(area);
  const count = results?.nbHits ?? 0;
  const countText = `${count} Pisos ${typeText} en ${areaText}`;

  return (
    <ListingViewBar
      className={className}
      countText={countText}
      oneCol={oneCol}
      onChange={onChange}
      sortSlot={
        <SortBySelect
          indexName={INDEX_NAME}
          show={generatedListingConfig.showSort}
        />
      }
    />
  );
};

/**
 * Precio mín. / máx. — dos selects independientes sobre el atributo de precio
 * (`importeVenta` en venta, `importeRecibofinal` en alquiler/temporal) vía el
 * conector `useNumericRangeFilter`. El widget se monta siempre (aunque el
 * chrome extendido esté off) para que `?precio-min`/`?precio-max` de la URL se
 * apliquen en cualquier tenant.
 */
const PriceFilters: FunctionComponent<{
  type: TypeFlat['ventaAlquiler'];
  fieldClass?: string;
}> = ({ type, fieldClass = FILTER_WIDTH }) => {
  const attribute = type === 'VENTA' ? 'importeVenta' : 'importeRecibofinal';
  const {
    start: [min, max],
    refine,
  } = useNumericRangeFilter({ attribute });
  const options = priceFilterOptions(type);

  return (
    <>
      <CustomSelect
        name="minPrice"
        options={options}
        setterFunction={(v: number) => refine([v || undefined, max])}
        placeholder={
          generatedListingConfig.filtersGrid ? 'Precio mín.' : 'Precio mínimo'
        }
        selected={min ?? 0}
        className={fieldClass}
      />
      <CustomSelect
        name="maxPrice"
        options={options}
        setterFunction={(v: number) => refine([min, v || undefined])}
        placeholder={
          generatedListingConfig.filtersGrid ? 'Precio máx.' : 'Precio máximo'
        }
        selected={max ?? 0}
        className={fieldClass}
      />
    </>
  );
};

/**
 * Tamaño mín. (m² computables). Widget siempre montado; el select sólo se
 * muestra con el chrome extendido (`show`). Así `?metros=80` filtra aunque el
 * tenant no muestre el control.
 */
const SizeFilter: FunctionComponent<{ show: boolean; fieldClass?: string }> = ({
  show,
  fieldClass = FILTER_WIDTH,
}) => {
  const {
    start: [min],
    refine,
  } = useNumericRangeFilter({ attribute: 'metrosComputablesUA' });

  if (!show) return null;

  return (
    <CustomSelect
      name="tamanoMin"
      options={M2_MIN_OPTIONS}
      setterFunction={(v: number) => refine([v || undefined, undefined])}
      placeholder="Tamaño mín."
      selected={min ?? 0}
      formatOption={(v) => `Desde ${v} m²`}
      className={fieldClass}
    />
  );
};

/**
 * Habitaciones (match exacto) vía `useMenu` sobre el facet `habitaciones`.
 * Widget siempre montado; el select sólo con el chrome extendido.
 */
const RoomsFilter: FunctionComponent<{
  show: boolean;
  fieldClass?: string;
}> = ({ show, fieldClass = FILTER_WIDTH }) => {
  const { items, refine } = useMenu({ attribute: 'habitaciones' });
  const refined = items.find((it) => it.isRefined);
  const selected = refined ? Number(refined.value) : 0;

  if (!show) return null;

  return (
    <CustomSelect
      name="habitaciones"
      options={HABITACIONES_OPTIONS}
      // `useMenu` es single-select: refinar un valor sustituye al anterior;
      // refinar el activo lo limpia. "Sin definir" (v=0) limpia el actual.
      setterFunction={(v: number) => {
        if (!v) {
          if (refined) refine(refined.value);
          return;
        }
        refine(String(v));
      }}
      placeholder="Habitaciones"
      selected={selected}
      formatOption={(v) => `${v} ${v === 1 ? 'habitación' : 'habitaciones'}`}
      className={fieldClass}
    />
  );
};

/**
 * Filtros en mobile como modal (Opción tenant `mobileFiltersModal`): un único
 * botón "Filtros" abre un bottom-sheet con todos los filtros apilados, en vez
 * de la fila que scrollea. Los filtros aplican en vivo; el footer sólo cierra
 * ("Ver N resultados") o limpia todo.
 *
 * Los widgets son los MISMOS que en la barra de desktop y comparten estado de
 * Algolia (react-instantsearch admite el mismo atributo en varias UIs). La
 * barra de desktop se oculta en mobile por CSS cuando este modal está activo.
 */
// Ancho de cada filtro dentro del modal: full-width, y `[&_button]:w-full!`
// fuerza también el botón-disparador interno (CustomSelect trae `w-48`,
// FacetSelect `w-max`) que si no se quedaría a su ancho natural.
const MODAL_FIELD = 'w-full [&_button]:w-full!';

const MobileFiltersModal: FunctionComponent<{
  type: TypeFlat['ventaAlquiler'];
  minBookingDays?: number;
  goToZone: (value: string | null) => void;
  zoneHref: (value: string | null) => string;
  className?: string;
}> = ({ type, minBookingDays, goToZone, zoneHref, className = '' }) => {
  const [open, setOpen] = useState(false);
  const { results } = useHits();
  const { refine: clearAll, canRefine: canClear } = useClearRefinements();
  const count = results?.nbHits ?? 0;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        // `py-3` iguala la altura (50px) del toggle mapa/lista; `px-3 w-max`
        // mantiene el botón compacto de ancho.
        className="border-input-border text-text-primary text-body text-body-s inline-flex w-max items-center gap-1.5 rounded-full border px-3 py-3 whitespace-nowrap"
      >
        <Filters className="h-4 w-4" aria-hidden="true" />
        <span>Filtros</span>
      </button>

      <Modal
        isOpen={open}
        closeModal={() => setOpen(false)}
        title="Filtros"
        // Pantalla completa en mobile: fija arriba y abajo, sin bordes
        // redondeados ni tope de altura del bottom-sheet. `contentClassName`
        // hace que el área de contenido llene el alto para poder repartir
        // filtros (centrados) y botones (abajo). El fondo usa `--page-bgcolor`
        // (el mismo del sitio) en lugar del blanco del modal.
        className="top-0 max-h-none! rounded-none! bg-[var(--page-bgcolor,#fff)]!"
        contentClassName="flex flex-1 flex-col"
      >
        {/* Filtros centrados verticalmente en el espacio libre; si no caben,
            scrollean. */}
        <div className="flex flex-1 flex-col justify-center gap-3 overflow-y-auto py-2">
          {isMedEst(type) && minBookingDays && (
            <DateFilter minBookingDays={minBookingDays} />
          )}
          <PriceFilters type={type} fieldClass={MODAL_FIELD} />
          <HierarchicalMenu
            attributes={ZONE_ATTRIBUTES}
            className={MODAL_FIELD}
            classNames={{ button: '', panel: '' }}
            sortBy={['isRefined']}
            navigate={goToZone}
            hrefFor={zoneHref}
          />
          <FacetSelect
            attribute="tipoDeUsoDescription"
            placeholder="Tipo de casa"
            className={MODAL_FIELD}
          />
          <RoomsFilter show fieldClass={MODAL_FIELD} />
          <SizeFilter show fieldClass={MODAL_FIELD} />
          <FacetSelect
            attribute="banos"
            placeholder="Baños"
            formatLabel={(label) =>
              `${label} ${label === '1' ? 'baño' : 'baños'}`
            }
            className={MODAL_FIELD}
          />
          <FacetSelect
            attribute="estadoDescripcion"
            placeholder="Estado"
            className={MODAL_FIELD}
          />
          <FacetMultiSelect
            attribute="caracteristicasFacet.description"
            placeholder="Características"
            hideValues={CARACTERISTICAS_OCULTAS}
            formatLabel={cleanCaracteristicaLabel}
            className={MODAL_FIELD}
          />
        </div>

        {/* Botones en una columna, abajo del todo. */}
        <div className="border-neutrals-100 flex flex-col gap-3 border-t pt-4">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="bg-secondary-500 text-neutrals-0 text-body text-body-s w-full rounded-full py-3 font-bold hover:bg-[rgb(var(--tw-color-button-bg-hover,var(--tw-color-secondary-400)))]"
          >
            Ver {count} resultados
          </button>
          <button
            type="button"
            onClick={() => clearAll()}
            disabled={!canClear}
            className="border-neutrals-500 text-text-primary text-body text-body-s w-full rounded-full border py-3 font-semibold disabled:opacity-40"
          >
            Limpiar
          </button>
        </div>
      </Modal>
    </div>
  );
};

/**
 * SearchBox headless: registra el widget de query para que el slug `?q=`
 * (texto libre del hero) se aplique como búsqueda de Algolia. Sin un widget
 * de SearchBox montado, el uiState `query` no llega a los searchParameters.
 */
const QuerySync: FunctionComponent = () => {
  useSearchBox();
  return null;
};

/**
 * Filtro de disponibilidad (MED_EST). El estado vive en el conector
 * `useMedEstDates` (→ URL `?desde=&hasta=` vía routing), no en `router.replace`
 * como antes, para no competir con el router de InstantSearch por el query
 * string.
 */
const DateFilter: FunctionComponent<{ minBookingDays: number }> = ({
  minBookingDays,
}) => {
  const { start, end, refine, clear } = useMedEstDates();
  const [open, setOpen] = useState(false);
  const hasDates = Boolean(start && end);

  return (
    <>
      <Modal
        isOpen={open}
        closeModal={() => setOpen(false)}
        unmount
        className="md:w-[710px]"
      >
        <CalendarTabs
          minDays={minBookingDays}
          onSubmit={(values) => {
            refine(values.startDate!, values.endDate!);
            setOpen(false);
          }}
          onReset={() => clear()}
          message={`Estancia mínima ${minBookingDays} días`}
        />
      </Modal>

      <button
        onClick={() => (hasDates ? clear() : setOpen(true))}
        className="border-neutrals-500 inline-flex w-max items-center justify-center gap-1.5 rounded-full border px-4 py-3 font-normal"
      >
        <Calendar className="h-4 w-4" />

        {!hasDates ? (
          'Disponibilidad'
        ) : (
          <>
            Del {format(start!, 'dd/MM/yyyy')} al {format(end!, 'dd/MM/yyyy')}
            <X className="h-4 w-4" />
          </>
        )}
      </button>
    </>
  );
};

export const ListOfFlats: FunctionComponent<TypeListOfFlatsProps> = ({
  minBookingDays,
  serverState,
  serverUrl,
  type,
  defaultArea,
  typeText,
  initialUiState,
  listingHeaderEnabled = false,
  eyebrow,
  titleTemplate,
  titleEstilo,
}: TypeListOfFlatsProps) => {
  const [mapFull, setMapFull] = useState(false);
  const [mapBounds, setMapBounds] = useState<any>(null);
  const [shouldApplyBoundsFilter, setShouldApplyBoundsFilter] = useState(false);
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  const [forceOneCol, setForceOneCol] = useState(false);
  // Segunda fila de filtros (Baños, Estado, Características): plegada por
  // defecto para que la barra ocupe una sola línea, como el Figma.
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const filterRef = useRef<HTMLDivElement>(null);
  const prevAmount = usePrevious(mapFull);
  const entry = useResizeObserver(filterRef);

  // El mapa está montado siempre, pero en mobile va oculto (`hidden lg:flex`)
  // salvo que se abra a pantalla completa. El filtro "buscar en esta zona del
  // mapa" sólo tiene sentido con el mapa a la vista: fuera de eso ignoramos los
  // bounds (Opción A). Evita que un mapa oculto 0×0 deje el listado a 0 hits.
  const isDesktop = useIsDesktop();
  const mapVisible = isDesktop || mapFull;

  // Tenant con filtros-en-modal en mobile. Sólo aplica con el chrome extendido
  // (los filtros con UI). En mobile se oculta la fila que scrollea y se muestra
  // un único botón "Filtros"; en desktop la barra es la de siempre.
  const useMobileFiltersModal =
    generatedListingConfig.mobileFiltersModal && listingHeaderEnabled;

  const mapClasses = oneLine`
    ${
      mapFull
        ? 'w-full aspect-3/4 md:aspect-[2/1] md:max-h-[80vh]'
        : 'min-w-[550px] aspect-3/4 hidden lg:flex'
    }
    h-full transition-all duration-300 ease-in-out
  `;

  // Routing de dos vías: la zona (`zoneHierarchical`) se lee del initialUiState
  // sembrado por `getServerSideProps` desde el path; el stateMapping la
  // re-inyecta para que no la pise el merge shallow del router middleware.
  const zoneHierarchical = (initialUiState as any)?.[INDEX_NAME]
    ?.hierarchicalMenu?.['hierarchicalZonas.lvl0']?.[0] as string | undefined;
  const routing = useMemo(
    () =>
      buildFlatsRouting({
        serverUrl,
        indexName: INDEX_NAME,
        type,
        zoneHierarchical,
      }),
    [serverUrl, type, zoneHierarchical],
  );

  // Cambiar de zona navega (SSR) al path de esa zona conservando los filtros
  // actuales (todo el query string menos el propio path, `categories`).
  // `value` es el valor jerárquico destino ("madrid > barcelona") o `null`
  // para quitar la zona (listado sin zona).
  const router = useRouter();
  const zoneHref = (value: string | null): string => {
    const path = value
      ? ROUTE_ZONE[type](convertToURL(value.replaceAll(' > ', '/')))
      : ROUTE_INDEX[type];
    const filters = Object.entries(router?.query ?? {}).filter(
      ([key]) => key !== 'categories',
    );
    const qs = new URLSearchParams(
      filters.flatMap(([key, v]) =>
        v == null
          ? []
          : Array.isArray(v)
            ? v.map((x) => [key, x] as [string, string])
            : [[key, String(v)] as [string, string]],
      ),
    ).toString();
    return qs ? `${path}?${qs}` : path;
  };
  const goToZone = (value: string | null) => {
    router?.push(zoneHref(value));
  };

  useEffect(() => {
    if (prevAmount !== undefined) {
      gtmPush({
        event: 'map_interaction',
        map_action: mapFull ? 'open' : 'close',
      });
    }
  }, [mapFull]);

  return (
    // `key` por zona: InstantSearch sólo lee serverState/initialUiState al
    // montar, así que en la navegación client-side entre zonas (goToZone) el
    // dropdown se quedaba con la refinación de la zona anterior. Remontar con
    // la zona nueva aplica el estado fresco; el resto de filtros sobrevive
    // porque viaja en el query string y el routing lo re-lee al montar.
    <InstantSearchSSRProvider
      key={zoneHierarchical ?? 'all'}
      {...(serverState ? serverState : {})}
    >
      <InstantSearch
        indexName={INDEX_NAME}
        routing={routing}
        {...(initialUiState && { initialUiState })}
      >
        {/* Price > 0 goes in `filters`, not `numericFilters`: the Algolia helper
            refuses to mix the advanced API with the managed numericRefinements
            that `useNumericRangeFilter` sets on the same attribute. */}
        <Configure
          hitsPerPage={generatedListingConfig.hitsPerPage}
          filters={`tipoDeUsoDescription:Vivienda AND ventaAlquiler:${type} AND ${
            type === 'VENTA' ? 'importeVenta' : 'importeRecibofinal'
          } > 0`}
        />

        {/* Apply bounds filter when user interacts with map. Sólo con el mapa
            visible (ver `mapVisible`): en mobile solo-lista el mapa está oculto
            y sus bounds no deben recortar el listado. */}
        {mapVisible && mapBounds && shouldApplyBoundsFilter && (
          <Configure
            insideBoundingBox={[
              [mapBounds[1], mapBounds[0], mapBounds[3], mapBounds[2]],
            ]}
          />
        )}

        {/* Clear location refinements when applying bounds filter */}
        <ClearLocationRefinements
          shouldClear={mapVisible && shouldApplyBoundsFilter}
        />

        {/* Headless: hace que `?q=` filtre (el hero lo emite). */}
        <QuerySync />

        <Container
          wrapper
          padding=""
          className="scrollbar-hide z-10 max-w-full overflow-x-auto overflow-y-visible pt-4 lg:overflow-visible"
          // Modo tricky para mostrar select en mobile con overflow-x-auto habría que cambiarlo a un modal los select
          style={{
            marginBottom: `-${calculateHeight(
              entry?.target as HTMLElement | undefined,
            )}px`,
            paddingBottom: `${calculateHeight(
              entry?.target as HTMLElement | undefined,
            )}px`,
          }}
          classNameWrapper="flex-row! lg:justify-between lg:items-start max-w-(--listing-filters-max-width)! px-(--listing-filters-px)"
        >
          {/* En mobile la fila scrollea horizontal (comportamiento
              histórico); en lg, donde el overflow es visible, los filtros
              envuelven a una segunda línea para no desbordar la página ni
              tapar el toggle grid/mapa. */}
          {/* En mobile sigue siendo una fila que scrollea en horizontal. En lg,
              con `filtersGrid` (opción tenant), pasa a GRID de columnas
              iguales: con flex, el `shrink-0` que trae `FacetSelect` impedía
              encoger y los selectores se solapaban al faltar espacio. El grid
              además hace que la segunda fila caiga en las mismas columnas que
              la primera. Sin el flag se mantiene la fila flex histórica. */}
          <div
            // `lg:flex-1` no es decorativo: `auto-fit` con `1fr` necesita un
            // ancho definido. Sin él, el contenedor se dimensiona a su
            // contenido, el grid resuelve una sola columna y los filtros
            // salen apilados en vertical.
            className={oneLine`border-neutrals-200 relative order-2 ml-1.5 flex shrink-0 flex-row gap-2 border-l pr-3 pl-1.5 lg:order-1 lg:ml-0 lg:gap-4 lg:border-none lg:pl-0 ${
              generatedListingConfig.filtersGrid
                ? `lg:grid lg:min-w-0 lg:flex-1 lg:shrink lg:gap-y-3 lg:pr-4 ${GRID_COLS}`
                : 'lg:pr-0'
            } ${useMobileFiltersModal ? 'max-lg:hidden' : ''} `}
          >
            {isMedEst(type) && minBookingDays && (
              <DateFilter minBookingDays={minBookingDays} />
            )}

            {/* Filtro por precios */}
            <PriceFilters type={type} />

            <HierarchicalMenu
              ref={filterRef}
              attributes={ZONE_ATTRIBUTES}
              className={FILTER_WIDTH}
              classNames={{
                // Sin `md:max-w-max`: ese tope impedía que el botón ocupara
                // el ancho que le reparte el `flex-1` del contenedor.
                button: '',
                panel: 'md:max-w-max md:min-w-[330px]',
              }}
              sortBy={['isRefined']}
              navigate={goToZone}
              hrefFor={zoneHref}
            />

            {/* Tamaño y habitaciones: widgets siempre montados (para que la
                URL filtre en cualquier tenant); el select sólo con chrome. */}
            <SizeFilter show={listingHeaderEnabled} />

            {/* Widgets de facets SIEMPRE montados para que `?tipo`, `?banos`,
                `?estado` y `?caracteristicas` filtren de forma consistente en
                cualquier tenant; el control sólo se pinta con el chrome
                extendido. Los legacy mantienen su barra visible (precios +
                zona) — los facets quedan aplicables por URL pero sin UI. */}
            <FacetSelect
              attribute="tipoDeUsoDescription"
              placeholder="Tipo de casa"
              hidden={!listingHeaderEnabled}
              className={FILTER_WIDTH}
            />

            <RoomsFilter show={listingHeaderEnabled} />

            {/* Segunda fila. Se oculta con `display:none` en lugar de
                desmontarla: los widgets tienen que seguir registrados para que
                `?banos`, `?estado` y `?caracteristicas` de la URL filtren
                aunque el control no esté a la vista. `lg:basis-full` la empuja
                a su propia línea; en mobile continúa la fila que scrollea. */}
            <div
              // `contents` disuelve este contenedor: sus hijos pasan a ser
              // hijos directos de la barra, así que caen en las mismas
              // columnas del grid que la primera fila (y en mobile se suman a
              // la fila que scrollea). Cerrado, `hidden` los oculta sin
              // desmontarlos.
              className={showMoreFilters ? 'contents' : 'hidden'}
            >
              <FacetSelect
                attribute="banos"
                placeholder="Baños"
                formatLabel={(label) =>
                  `${label} ${label === '1' ? 'baño' : 'baños'}`
                }
                hidden={!listingHeaderEnabled}
                className={FILTER_WIDTH}
              />
              <FacetSelect
                attribute="estadoDescripcion"
                placeholder="Estado"
                hidden={!listingHeaderEnabled}
                className={FILTER_WIDTH}
              />
              <FacetMultiSelect
                attribute="caracteristicasFacet.description"
                placeholder="Características"
                hideValues={CARACTERISTICAS_OCULTAS}
                formatLabel={cleanCaracteristicaLabel}
                hidden={!listingHeaderEnabled}
                className={FILTER_WIDTH}
              />
            </div>
          </div>

          {/* "Más filtros" vive en el wrapper exterior, no dentro del grid:
              como celda del grid caía en medio de los selectores. Aquí el grid
              de filtros es `lg:flex-1` y absorbe el espacio, así que el botón
              queda pegado al final de la barra, junto al toggle de mapa.
              `lg:self-start` lo alinea con la primera fila de filtros. */}
          {listingHeaderEnabled && (
            <button
              type="button"
              onClick={() => setShowMoreFilters((s) => !s)}
              aria-expanded={showMoreFilters}
              className={oneLine`border-neutrals-500 text-text-primary hover:bg-neutrals-50 text-body text-body-s order-3 ml-3 flex shrink-0 items-center gap-2 rounded-full border px-4 py-3 whitespace-nowrap transition-colors lg:order-2 lg:ml-0 lg:self-start ${useMobileFiltersModal ? 'max-lg:hidden' : ''} `}
            >
              <Filters className="h-4 w-4" aria-hidden="true" />
              <span>{showMoreFilters ? 'Menos filtros' : 'Más filtros'}</span>
            </button>
          )}

          {/* Sólo mobile: botón único que abre el modal con todos los filtros. */}
          {useMobileFiltersModal && (
            <MobileFiltersModal
              type={type}
              minBookingDays={minBookingDays}
              goToZone={goToZone}
              zoneHref={zoneHref}
              className="order-2 ml-auto shrink-0 lg:hidden"
            />
          )}

          <div
            className={`${filtersBorderClass.toggle} relative order-1 ml-3 flex shrink-0 flex-row items-center justify-between rounded-full border p-1 lg:order-3 lg:ml-4`}
          >
            {/* Indicador deslizante. Algo más pequeño que la celda (h-9 en un
                botón h-10) y con `inset` de 2px para que no toque los bordes
                del pill; `translate-x-10` lo mueve a la celda de la derecha. */}
            <div
              className={oneLine`bg-secondary-500 absolute top-1.5 left-1.5 -z-10 h-9 w-9 rounded-full transition-transform duration-400 ease-out ${!mapFull ? 'translate-x-10' : ''} `}
            />

            <button
              type="button"
              aria-label="Ver mapa"
              aria-pressed={mapFull}
              onClick={() => setMapFull(true)}
              className="text-text-neutrals-900 flex h-10 w-10 items-center justify-center rounded-full"
            >
              <MapPin className="h-4 w-4" />
            </button>

            <button
              type="button"
              aria-label="Ver listado"
              aria-pressed={!mapFull}
              onClick={() => setMapFull(false)}
              className="text-text-neutrals-900 flex h-10 w-10 items-center justify-center rounded-full"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </Container>

        <Container
          wrapper
          classNameWrapper={oneLine`
            lg:flex-row! gap-4 lg:gap-(--listing-grid-gap)
            max-w-(--listing-wrapper-max-width)!
            md:pl-(--listing-wrapper-px)
            ${mapFull ? 'md:pr-(--listing-wrapper-px)' : ''}
          `}
          className={`${filtersBorderClass.divider} mt-4 border-t py-(--listing-grid-py-mobile)! md:py-(--listing-grid-py-desktop)!`}
        >
          <div className={mapFull ? 'lg:hidden' : 'flex-1'}>
            {listingHeaderEnabled ? (
              <>
                <ChromeHeader
                  defaultArea={defaultArea}
                  typeText={typeText}
                  eyebrow={eyebrow}
                  titleTemplate={titleTemplate}
                  titleEstilo={titleEstilo}
                />
                <ChromeViewBar
                  className="mt-6 lg:mt-8"
                  defaultArea={defaultArea}
                  typeText={typeText}
                  oneCol={forceOneCol}
                  onChange={setForceOneCol}
                />
              </>
            ) : (
              <Title
                title={(currentArea) =>
                  userHasInteracted
                    ? `Pisos ${typeText}`
                    : `Pisos ${typeText} en ${currentArea}`
                }
                defaultArea={defaultArea}
                attributes={ZONE_ATTRIBUTES}
              />
            )}

            <GridCardHits
              forceOneCol={listingHeaderEnabled && forceOneCol}
              redesign={listingHeaderEnabled}
              className={mapFull ? 'hidden lg:block' : 'mt-4'}
            />

            <Pagination
              className={oneLine`mt-6 shrink-0 justify-end ${mapFull ? 'hidden lg:block' : ''} `}
            />
          </div>

          <div
            className={`${mapClasses} rounded-custom sticky top-6 isolate overflow-hidden`}
          >
            <button
              className={oneLine`bg-neutrals-0 text-primary-500 shadow-light-s absolute top-6 left-6 z-10 flex items-center justify-center rounded-full ${mapFull ? '' : 'h-10 w-10'} `}
              onClick={() => setMapFull(!mapFull)}
            >
              {mapFull ? (
                <div className="text-text-neutrals-900 flex flex-row gap-3 py-2.5 pr-2 pl-4">
                  Ver listado{' '}
                  <ArrowRight className="text-primary-500 h-6 w-6" />
                </div>
              ) : (
                <ArrowLeft className="h-6 w-6" />
              )}
            </button>

            <ClusteringMapAlgolia
              className={mapClasses}
              mapBounds={mapBounds}
              setMapBounds={setMapBounds}
              shouldApplyBoundsFilter={shouldApplyBoundsFilter}
              setShouldApplyBoundsFilter={setShouldApplyBoundsFilter}
              onUserInteraction={() => setUserHasInteracted(true)}
            />
          </div>
        </Container>
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

ListOfFlats.displayName = 'ListOfFlats';
